
import React, { useState } from 'react';
import { supabase } from '../services/supabaseClient';
import { UserProfile } from '../types';

interface AuthModalProps {
  onClose: () => void;
  onSuccess: (user: UserProfile) => void;
  onNewUser: () => void;
}

export const AuthModal: React.FC<AuthModalProps> = ({ onClose, onSuccess, onNewUser }) => {
  const [handle, setHandle] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!handle || !password) {
      setError('Введите логин и пароль');
      return;
    }
    
    setLoading(true);
    setError('');
    
    const formattedHandle = '@' + handle.trim().replace('@', '').toLowerCase();

    try {
      const { data, error: fetchError } = await supabase
        .from('profiles')
        .select('*')
        .ilike('tg_handle', formattedHandle)
        .single();

      if (fetchError || !data) {
        setError('Пользователь не найден.');
      } else if (data.password && data.password !== password) {
        setError('Неверный пароль.');
      } else {
        // Гарантированно убираем gender, чтобы не вызвать ошибку TS
        const user: UserProfile = {
          id: data.id,
          name: data.name || '',
          photo: data.photo || '',
          tgHandle: data.tg_handle || '',
          password: data.password || '',
          city: data.city || '',
          activitySphere: data.activity_sphere || '',
          interests: data.interests || [],
          goal: data.goal || 'both',
          format: data.format || 'both',
          aboutMe: data.about_me || '',
          canHelpWith: data.can_help_with || '',
          lookingFor: data.looking_for || '',
          role: data.role || 'user',
          visibility: data.visibility || 'community_only',
          bot_connected: data.bot_connected
        };
        onSuccess(user);
      }
    } catch (err) {
      setError('Ошибка при входе. Попробуйте позже.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[150] flex items-center justify-center p-4 bg-gray-900/90 backdrop-blur-md" onClick={onClose}>
      <div className="bg-white rounded-[3.5rem] p-12 max-w-md w-full shadow-2xl border border-red-50 relative animate-fadeIn" onClick={e => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-8 right-8 text-gray-300 hover:text-red-600 transition-colors">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M6 18L18 6M6 6l12 12" /></svg>
        </button>
        
        <div className="text-center mb-10">
          <div className="w-20 h-20 bg-red-600 rounded-[2rem] mx-auto flex items-center justify-center mb-8 shadow-2xl shadow-red-600/20">
            <span className="text-white font-black text-3xl">V</span>
          </div>
          <h3 className="text-3xl font-black text-gray-900 uppercase tracking-tighter">Вход в клуб</h3>
        </div>

        <form onSubmit={handleLogin} className="space-y-6">
          <div>
            <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3 ml-2">Ник в Telegram</label>
            <div className="relative">
              <span className="absolute left-6 top-1/2 -translate-y-1/2 font-black text-lg text-gray-300">@</span>
              <input 
                type="text"
                placeholder="username"
                className="w-full pl-12 pr-6 py-5 bg-gray-50 border border-gray-100 rounded-3xl outline-none focus:ring-2 focus:ring-red-500 font-black text-lg"
                value={handle.replace('@', '')}
                onChange={(e) => setHandle(e.target.value)}
                disabled={loading}
              />
            </div>
          </div>

          <div>
            <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3 ml-2">Пароль</label>
            <div className="relative">
              <input 
                type={showPassword ? "text" : "password"}
                placeholder="••••••••"
                className="w-full px-6 py-5 bg-gray-50 border border-gray-100 rounded-3xl outline-none focus:ring-2 focus:ring-red-500 font-black text-center text-lg"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
              />
              <button 
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-6 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-600 transition-colors"
              >
                {showPassword ? (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>
                ) : (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l18 18"/></svg>
                )}
              </button>
            </div>
          </div>

          {error && <p className="text-red-500 text-[10px] font-black uppercase text-center tracking-widest">{error}</p>}

          <button 
            type="submit"
            disabled={loading}
            className="w-full mt-4 py-6 bg-black text-white font-black rounded-3xl hover:bg-red-600 transition-all shadow-xl shadow-red-600/10 uppercase tracking-[0.2em] text-xs"
          >
            {loading ? 'ВХОД...' : 'ВОЙТИ'}
          </button>
        </form>

        <div className="mt-12 text-center">
          <button onClick={onNewUser} className="text-red-600 font-black uppercase text-[10px] tracking-widest hover:text-black transition-colors border-b-2 border-red-600/20">
            Создать новую анкету
          </button>
        </div>
      </div>
    </div>
  );
};
